import { useEffect, useState } from 'preact/hooks';
import {
  ApiError,
  draftSubmissionMessage,
  getMailbox,
  getPlan,
  getPortfolio,
  getRecord,
  getSubjectPageState,
  getSubmission,
  listAllRecordsWithLinks,
  listSubmissionLinksApi,
  markSubmissionSentByHand,
  mintSubmissionLinkApi,
  reissueSubmissionLinkApi,
  saveSubmissionCoverNote,
  sendSubmissionEmail,
  setSubmissionMaterials,
  transitionRecord,
  linkRecord,
  convertSubmissionToAudition,
  type PortfolioApi,
  type PortfolioGroupApi,
  type PortfolioItemApi,
  type PlanApi,
  type RecordRow,
  type SubmissionApi,
  type SubmissionDraftApi,
  type SubmissionLinkSummaryApi,
} from '../lib/api';
import { useRecordDetail } from '../lib/useRecordDetail';
import { useRecordsVersion, bumpRecordsVersion } from '../lib/recordEditor';
import { describeReadFailure, type ReadFailure } from '../lib/readFailure';
import { humanizeValue } from '../lib/fieldDisplay';
import { AA, LINK } from '../lib/auditionSpread';
import { disciplineOf } from '../lib/discipline';
import {
  PROFILE_CURRENT_REEL,
  SUBMISSION_LINK,
  SUBMISSION_TYPE,
  otherEndOf,
  strField,
  submissionSteps,
} from '../lib/submissions';
import './spread.css';
import './submissionSpread.css';

// B20a §4-§8 — THE SUBMISSION PAGE. Same furniture as AuditionSpread —
// useRecordDetail for schema+record+links, `stepsFor`'s fetched-lifecycle
// stepper, the doMove/askPassed/putAway/undoStageMove shape for B16 §7's
// Passed/"No word" pair and its receipt+Undo — never re-invented, per the
// brief's own instruction. What follows adds the parts AuditionSpread has
// no analogue for: the package (§5), the composer (§6), links/receipts
// (§7) and the by-hand send (§8), each reading the actor-specific
// `GET /api/actor/submissions/:id` (provenance/canSend/replies) beside the
// generic record read.

export interface SubmissionSpreadProps {
  tenantId: string;
  id: string;
  onOpenAudition: (auditionId: string) => void;
  onBackToList: () => void;
  /** §7 — a reply "opens the source" (the raw message a reply-matched
   * `aa_source` filed). Same door AuditionDetail's own `.srclink` chip
   * already opens. */
  onOpenSource: (sourceId: string) => void;
}

const GROUP_LABEL: Record<PortfolioGroupApi, string> = {
  headshot: 'Headshots',
  reel: 'Reels',
  resume: 'Résumé',
  other: 'Other',
};

function groupOrder(): PortfolioGroupApi[] {
  return ['headshot', 'reel', 'resume', 'other'];
}

export function SubmissionSpread({ tenantId, id, onOpenAudition, onBackToList, onOpenSource }: SubmissionSpreadProps) {
  const { schema, record, links, error, reload } = useRecordDetail(tenantId, SUBMISSION_TYPE, id);
  const [failure, setFailure] = useState<ReadFailure | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const recordsVersion = useRecordsVersion();

  const [api, setApi] = useState<SubmissionApi | null>(null);
  const [plan, setPlan] = useState<PlanApi | null>(null);
  const [mailbox, setMailbox] = useState<string | null>(null);

  const [role, setRole] = useState<RecordRow | null>(null);
  const [project, setProject] = useState<RecordRow | null>(null);
  const [contact, setContact] = useState<RecordRow | null>(null);
  const [subject, setSubject] = useState<RecordRow | null>(null);
  const [pageSlug, setPageSlug] = useState<string | null>(null);

  const [portfolio, setPortfolio] = useState<PortfolioApi | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[] | null>(null);
  const [materialsSay, setMaterialsSay] = useState<string | null>(null);

  const [draft, setDraft] = useState<SubmissionDraftApi | null>(null);
  // The EDITABLE part is the message only (B20c §6) — never the whole email.
  const [draftMessage, setDraftMessage] = useState('');
  const [draftBusy, setDraftBusy] = useState(false);
  const [draftSay, setDraftSay] = useState<string | null>(null);
  const [messageSaving, setMessageSaving] = useState(false);

  const [linkSummaries, setLinkSummaries] = useState<SubmissionLinkSummaryApi[] | null>(null);

  // Stage-move furniture — the exact AuditionSpread shape.
  const [confirmStage, setConfirmStage] = useState<string | null>(null);
  const [stageReceipt, setStageReceipt] = useState<{ text: string; from: string; to: string; canUndo: boolean } | null>(
    null
  );
  const [passedOutcome, setPassedOutcome] = useState('');

  const [convertSay, setConvertSay] = useState<string | null>(null);
  const [convertedAuditionId, setConvertedAuditionId] = useState<string | null>(null);
  const [pickingRole, setPickingRole] = useState(false);
  const [pickableRoles, setPickableRoles] = useState<RecordRow[] | null>(null);

  const subjectId = record ? otherEndOf(links, record.id, SUBMISSION_LINK.SUBJECT) : null;
  const roleId = record ? otherEndOf(links, record.id, SUBMISSION_LINK.ROLE) : null;
  const projectId = record ? otherEndOf(links, record.id, SUBMISSION_LINK.PROJECT) : null;
  const contactId = record ? otherEndOf(links, record.id, SUBMISSION_LINK.CONTACT) : null;

  // ---- the read side ----
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setFailure(null);
      try {
        const submissionApi = await getSubmission(tenantId, id);
        if (cancelled) return;
        setApi(submissionApi);
      } catch (err) {
        if (cancelled) return;
        setFailure(describeReadFailure(err));
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [tenantId, id, recordsVersion]);

  useEffect(() => {
    // Side reads: a plan that cannot be read leaves the composer read-only
    // (the safer default, same posture AuditionSpread's own craft-plan read
    // takes); a mailbox that cannot be read just omits the "Replies go to"
    // line. Neither degrades the submission's own read, which reports
    // loudly through `failure`/`error` above.
    void getPlan(tenantId)
      .then(setPlan)
      .catch(() => {
        setPlan(null);
      });
    void getMailbox(tenantId)
      .then(setMailbox)
      .catch(() => {
        setMailbox(null);
      });
  }, [tenantId]);

  useEffect(() => {
    let cancelled = false;
    async function loadAround() {
      // Each is a side read for the hero line only — a role/project/contact/
      // subject that cannot be read degrades that one name to absent; the
      // submission itself still renders (its own read reports loudly above).
      const [r, p, c, s] = await Promise.all([
        roleId
          ? getRecord(tenantId, roleId).catch(() => {
              return null;
            })
          : Promise.resolve(null),
        projectId
          ? getRecord(tenantId, projectId).catch(() => {
              return null;
            })
          : Promise.resolve(null),
        contactId
          ? getRecord(tenantId, contactId).catch(() => {
              return null;
            })
          : Promise.resolve(null),
        subjectId
          ? getRecord(tenantId, subjectId).catch(() => {
              return null;
            })
          : Promise.resolve(null),
      ]);
      if (cancelled) return;
      setRole(r);
      setProject(p);
      setContact(c);
      setSubject(s);
    }
    void loadAround();
    return () => {
      cancelled = true;
    };
  }, [tenantId, roleId, projectId, contactId, subjectId, recordsVersion]);

  useEffect(() => {
    let cancelled = false;
    async function loadSubjectExtras() {
      if (!subjectId) {
        setPortfolio(null);
        setPageSlug(null);
        return;
      }
      try {
        const p = await getPortfolio(tenantId, subjectId);
        if (!cancelled) setPortfolio(p);
      } catch {
        if (!cancelled) setPortfolio(null);
      }
      try {
        const state = await getSubjectPageState(tenantId, subjectId);
        if (!cancelled) setPageSlug(state.page?.live ? state.page.slug : null);
      } catch {
        if (!cancelled) setPageSlug(null);
      }
    }
    void loadSubjectExtras();
    return () => {
      cancelled = true;
    };
  }, [tenantId, subjectId, recordsVersion]);

  useEffect(() => {
    let cancelled = false;
    void listSubmissionLinksApi(tenantId, id)
      .then((rows) => {
        if (!cancelled) setLinkSummaries(rows);
      })
      .catch(() => {
        if (!cancelled) setLinkSummaries([]);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, id, recordsVersion]);

  // Seed the chosen-materials tick state from the record's own links + its
  // saved order, and — on a submission with NOTHING chosen yet — apply the
  // discipline's own default (§5: "the part's discipline decides the
  // default ticks, never the options"). Applied once, guarded on
  // `selectedIds === null`, so a later manual change is never overwritten
  // by this effect re-running.
  useEffect(() => {
    if (selectedIds !== null || !record) return;
    const existingLinks = links.filter(
      (l) => l.src_id === record.id && l.link_type === SUBMISSION_LINK.MATERIALS && l.dst_type === AA.MEDIA
    );
    const order = Array.isArray(record.fields.materials_order)
      ? (record.fields.materials_order as unknown[]).filter((x): x is string => typeof x === 'string')
      : [];
    const linkedIds = existingLinks.map((l) => l.dst_id);
    if (linkedIds.length > 0) {
      const ordered = [...order.filter((mid) => linkedIds.includes(mid)), ...linkedIds.filter((mid) => !order.includes(mid))];
      setSelectedIds(ordered);
      return;
    }
    if (!portfolio) return; // wait for the portfolio before defaulting
    // No part, no default (the demo walk, 2026-09-27: "no default ticks
    // without a part"). The discipline is the part's, so with no part there
    // is no discipline to decide anything — nothing is ticked, nothing is
    // written, and `selectedIds` stays null so linking a part later still
    // applies that part's default. With a part linked but not yet read,
    // wait for it rather than default on a guess.
    if (!roleId || !role || role.id !== roleId) return;
    void applyDefaultTicks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record, links, portfolio, role, roleId, subjectId]);

  async function currentDefaultIds(): Promise<string[]> {
    if (!portfolio) return [];
    if (disciplineOf(role) === 'crew') {
      return portfolio.groups.resume.map((m) => m.id);
    }
    // Performance: the current headshot + the lead reel — aa_profile's own
    // pointers (profile_current_headshot / profile_current_reel), read for
    // THIS subject. Absent either pointer, that half is simply not offered
    // — never a guess at which headshot or reel is "the" one.
    if (!subjectId) return [];
    try {
      const profiles = await listAllRecordsWithLinks(tenantId, AA.PROFILE, {
        linkTypes: [LINK.PROFILE_SUBJECT, LINK.PROFILE_CURRENT_HEADSHOT, PROFILE_CURRENT_REEL],
      });
      const mine = profiles.records.find(
        (p) => otherEndOf(profiles.linksByRecordId[p.id] ?? [], p.id, LINK.PROFILE_SUBJECT) === subjectId
      );
      if (!mine) return [];
      const profileLinks = profiles.linksByRecordId[mine.id] ?? [];
      const headshotId = otherEndOf(profileLinks, mine.id, LINK.PROFILE_CURRENT_HEADSHOT);
      const reelId = otherEndOf(profileLinks, mine.id, PROFILE_CURRENT_REEL);
      return [headshotId, reelId].filter((x): x is string => Boolean(x));
    } catch {
      return [];
    }
  }

  async function applyDefaultTicks(): Promise<void> {
    const defaults = await currentDefaultIds();
    setSelectedIds(defaults);
    if (defaults.length > 0 && record) {
      try {
        await setSubmissionMaterials(tenantId, id, defaults);
        bumpRecordsVersion();
      } catch {
        // The default tick is a convenience, not a write the artist asked
        // for — a failure here is silent; the next manual toggle tries again.
      }
    }
  }

  async function toggleMaterial(mediaId: string): Promise<void> {
    const current = selectedIds ?? [];
    const next = current.includes(mediaId) ? current.filter((m) => m !== mediaId) : [...current, mediaId];
    setSelectedIds(next);
    try {
      const result = await setSubmissionMaterials(tenantId, id, next);
      setMaterialsSay(result.receipt);
      bumpRecordsVersion();
    } catch (err) {
      setMaterialsSay(err instanceof ApiError ? err.message : 'that could not be saved');
    }
  }

  async function reorder(mediaId: string, dir: -1 | 1): Promise<void> {
    const current = selectedIds ?? [];
    const at = current.indexOf(mediaId);
    if (at < 0) return;
    const swapWith = at + dir;
    if (swapWith < 0 || swapWith >= current.length) return;
    const next = [...current];
    const tmp = next[at]!;
    next[at] = next[swapWith]!;
    next[swapWith] = tmp;
    setSelectedIds(next);
    try {
      const result = await setSubmissionMaterials(tenantId, id, next);
      setMaterialsSay(result.receipt);
      bumpRecordsVersion();
    } catch (err) {
      setMaterialsSay(err instanceof ApiError ? err.message : 'that could not be saved');
    }
  }

  async function writeTheEmail(): Promise<void> {
    setDraftBusy(true);
    setDraftSay(null);
    try {
      const d = await draftSubmissionMessage(tenantId, id);
      setDraft(d);
      setDraftMessage(d.message);
    } catch (err) {
      setDraftSay(err instanceof ApiError ? err.message : 'the draft could not be written just now');
    } finally {
      setDraftBusy(false);
    }
  }

  // B20c §6 — Working+ saves the MESSAGE through PUT …/cover-note, then
  // re-drafts so `preview`/`copyText`/`linkToken` all reflect it: the
  // server's own composer is the only place the whole email is built.
  async function saveMessage(): Promise<void> {
    setMessageSaving(true);
    setDraftSay(null);
    try {
      await saveSubmissionCoverNote(tenantId, id, draftMessage);
      await writeTheEmail();
    } catch (err) {
      setDraftSay(err instanceof ApiError ? err.message : 'that message could not be saved');
    } finally {
      setMessageSaving(false);
    }
  }

  async function copyEmailInstead(): Promise<void> {
    if (!draft) return;
    const text = [`Subject: ${draft.subject}`, '', draft.copyText].join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setDraftSay('Copied — paste it into your own email.');
    } catch {
      setDraftSay('That could not be copied — select the text above by hand.');
    }
  }

  async function markSentByHand(): Promise<void> {
    try {
      const opportunity = strField(record, 'opportunity') ?? record?.display_name ?? '';
      await markSubmissionSentByHand(tenantId, id);
      setStageReceipt({ text: `Marked sent — ${opportunity}.`, from: 'draft', to: 'sent', canUndo: false });
      reload();
      bumpRecordsVersion();
    } catch (err) {
      setRefusal(err instanceof ApiError ? err.message : 'that could not be marked sent');
    }
  }

  // ---- stage moves — the AuditionSpread shape, unchanged ----
  const roleLabel = strField(record, 'opportunity') ?? record?.display_name ?? 'This submission';

  async function doMove(to: string, fields?: Record<string, unknown>, receiptText?: string) {
    setRefusal(null);
    setStageReceipt(null);
    const from = record!.lifecycle_state;
    try {
      await transitionRecord(tenantId, id, to, fields);
      const canUndo = Boolean(from && schema?.lifecycle?.transitions[to]?.includes(from));
      setStageReceipt({ text: receiptText ?? `${roleLabel} moved to ${humanizeValue(to)}.`, from: from ?? '', to, canUndo });
      reload();
      bumpRecordsVersion();
    } catch (err) {
      setRefusal(err instanceof ApiError ? err.message : 'that move was refused');
    }
  }

  function askPassed() {
    setRefusal(null);
    setStageReceipt(null);
    setPassedOutcome('');
    setConfirmStage('passed');
  }

  function confirmStageMove() {
    const to = confirmStage;
    if (!to) return;
    setConfirmStage(null);
    void doMove(to, to === 'passed' ? { outcome: passedOutcome.trim() } : undefined);
  }

  function putAway() {
    void doMove('archived', undefined, `${roleLabel} put away.`);
  }

  function undoStageMove() {
    if (!stageReceipt || !stageReceipt.canUndo) return;
    const { from, to } = stageReceipt;
    setStageReceipt(null);
    void doMove(from);
    // suppress unused warning for `to` — kept for parity with the shape.
    void to;
  }

  // ---- convert into an audition ----
  async function doConvert(): Promise<void> {
    setConvertSay(null);
    try {
      const result = await convertSubmissionToAudition(tenantId, id);
      setConvertedAuditionId(result.target.id);
      setConvertSay(`${role?.display_name ?? roleLabel} is now an audition.`);
      bumpRecordsVersion();
    } catch (err) {
      if (err instanceof ApiError) {
        setConvertSay(err.serverMessage ?? err.message);
        if (err.code === 'validation_error') setPickingRole(true);
      } else {
        setConvertSay('that could not be converted just now');
      }
    }
  }

  async function loadPickableRoles(): Promise<void> {
    if (pickableRoles) return;
    const rows = await listAllRecordsWithLinks(tenantId, AA.ROLE);
    setPickableRoles(rows.records);
  }

  async function pickRole(pickedRoleId: string): Promise<void> {
    try {
      await linkRecord(tenantId, id, SUBMISSION_LINK.ROLE, pickedRoleId);
      setPickingRole(false);
      bumpRecordsVersion();
      await doConvert();
    } catch (err) {
      setConvertSay(err instanceof ApiError ? err.message : 'that role could not be added');
    }
  }

  async function reissueLink(grantId: string): Promise<void> {
    try {
      await reissueSubmissionLinkApi(tenantId, grantId);
      const rows = await listSubmissionLinksApi(tenantId, id);
      setLinkSummaries(rows);
    } catch {
      // the list simply does not change — the row's own status still reads
      // expired/revoked, which is the honest state to leave it in.
    }
  }

  if (error) {
    return (
      <div class="sp">
        <div class="sp__eyebrow">Submission</div>
        <div class="sp__say">
          {error.headline}
          <br />
          <span class="sp__say-soft">{error.detail}</span>
        </div>
        <button type="button" class="sp__retry" onClick={onBackToList}>
          Back to submissions
        </button>
      </div>
    );
  }

  if (!record || !schema) {
    return (
      <div class="sp">
        <div class="sp__eyebrow">Submission</div>
        <div class="sp__say">One moment…</div>
      </div>
    );
  }

  const opportunity = strField(record, 'opportunity') ?? record.display_name;
  const heroTitle = role?.display_name ?? opportunity;
  const steps = submissionSteps(schema.lifecycle, record.lifecycle_state);
  const legalNext = schema.lifecycle?.transitions[record.lifecycle_state ?? ''] ?? [];
  const draftEditable = plan?.key !== 'free';
  const canSend = api?.canSend;
  const isFree = plan?.key === 'free';

  const materialsFileIds = (selectedIds ?? [])
    .map((mid) => portfolio?.items.find((it) => it.id === mid))
    .filter((it): it is PortfolioItemApi => Boolean(it && it.hasFile))
    .map((it) => it.id);

  return (
    <div class="sp">
      {/* B21 (2026-09-27) — the breadcrumb and back go to Auditions, not
          "Submissions": the list is a filter on that screen now, not a
          screen of its own (HouseholdAuditions.tsx). `onBackToList`
          already lands there with the Submissions chip selected. */}
      <div class="sp__crumb">
        <button type="button" onClick={onBackToList}>
          Auditions
        </button>
        <span class="sp__crumb-sep">›</span>
        <span class="sp__crumb-cur">this submission</span>
      </div>
      <div class="sp__eyebrow">Submission</div>

      {/* §4 — THE HERO */}
      <h1 class="sub__hero-title">{heroTitle}</h1>
      <p class="sub__hero-sub">
        {[project?.display_name, contact?.display_name].filter(Boolean).join(' · ') || ' '}
      </p>

      {failure ? (
        <p class="sp__say sp__say--loud">
          {failure.headline} {failure.detail}
        </p>
      ) : null}
      {refusal ? <p class="sp__say sp__say--loud">{refusal}</p> : null}

      {/* THE STAGE ROW — B16 §7's own shape, unchanged. */}
      <div class="sp__stepper">
        {steps.map((step) => {
          const can = legalNext.includes(step.state);
          return (
            <div key={step.state} class="sp__step-wrap">
              <button
                type="button"
                class={`sp__step${step.reached ? ' sp__step--reached' : ''}${step.current ? ' sp__step--current' : ''}${
                  step.offSpine ? ' sp__step--off' : ''
                }`}
                disabled={!can}
                onClick={() => void doMove(step.state)}
              >
                <span class="sp__dot" />
                <span class="sp__step-tx">{step.label}</span>
              </button>
            </div>
          );
        })}
      </div>

      {!confirmStage && legalNext.includes('passed') ? (
        <button type="button" class="sp__inline-act" data-testid="stage-ask-passed" onClick={askPassed}>
          Passed
        </button>
      ) : null}
      {!confirmStage && legalNext.includes('archived') ? (
        <button type="button" class="sp__inline-act" data-testid="stage-put-away" onClick={putAway}>
          No word — put it away
        </button>
      ) : null}
      {confirmStage ? (
        <p class="sp__age" data-testid="stage-confirm">
          Mark {roleLabel} passed?
          {' '}
          What did they say?
          <br />
          <input
            type="text"
            class="sp__outcome-input"
            data-testid="stage-confirm-outcome"
            value={passedOutcome}
            onInput={(e) => setPassedOutcome((e.target as HTMLInputElement).value)}
          />
          <br />
          <button
            type="button"
            class="sp__inline-act"
            data-testid="stage-confirm-yes"
            disabled={passedOutcome.trim().length === 0}
            onClick={confirmStageMove}
          >
            Yes
          </button>{' '}
          <button type="button" class="sp__inline-act" data-testid="stage-confirm-no" onClick={() => setConfirmStage(null)}>
            Not yet
          </button>
        </p>
      ) : null}
      {stageReceipt ? (
        <p class="sp__age" data-testid="stage-receipt">
          {stageReceipt.text}
          {stageReceipt.canUndo ? (
            <>
              {' '}
              <button type="button" class="sp__inline-act" data-testid="stage-undo" onClick={undoStageMove}>
                Undo
              </button>
            </>
          ) : null}
        </p>
      ) : null}

      {/* B20a §4 — INVITED: "Make it an audition". */}
      {record.lifecycle_state === 'invited' && !convertedAuditionId ? (
        <div class="sub__section">
          <button type="button" class="sp__act" data-testid="convert-audition" onClick={() => void doConvert()}>
            Make it an audition
          </button>
          {convertSay ? <p class="sp__age">{convertSay}</p> : null}
          {pickingRole ? (
            <div>
              <button type="button" class="sp__inline-act" onClick={() => void loadPickableRoles()}>
                Pick a part
              </button>
              {pickableRoles ? (
                <ul class="sub__pick-list">
                  {pickableRoles.map((r) => (
                    <li key={r.id}>
                      <button type="button" class="sp__inline-act" onClick={() => void pickRole(r.id)}>
                        {r.display_name}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
      {convertedAuditionId ? (
        <div class="sub__section">
          <p class="sp__age">{convertSay}</p>
          <button type="button" class="sp__act" onClick={() => onOpenAudition(convertedAuditionId)}>
            Open the audition
          </button>
        </div>
      ) : null}

      {/* §8 — MARK IT SENT BY HAND */}
      {record.lifecycle_state === 'draft' ? (
        <div class="sub__section">
          <button type="button" class="sp__inline-act" data-testid="send-by-hand" onClick={() => void markSentByHand()}>
            I sent it myself
          </button>
        </div>
      ) : null}

      {/* §5 — THE PACKAGE */}
      <h2 class="sub__section-title">What to send</h2>
      {!subjectId ? (
        <p class="sp__say">This submission has no performer yet — link one before choosing what to send.</p>
      ) : !portfolio ? (
        <p class="sp__say">One moment…</p>
      ) : (
        <div class="sub__package">
          {groupOrder().map((group) => {
            const items = portfolio.groups[group];
            if (items.length === 0) return null;
            return (
              <div key={group} class="sub__group">
                <h3 class="sub__group-title">{GROUP_LABEL[group]}</h3>
                {items.map((item) => {
                  const checked = (selectedIds ?? []).includes(item.id);
                  const pos = (selectedIds ?? []).indexOf(item.id);
                  return (
                    <div key={item.id} class="sub__material-row">
                      <label>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => void toggleMaterial(item.id)}
                        />
                        {item.label}
                      </label>
                      {checked ? (
                        <span class="sub__order-btns">
                          <button type="button" onClick={() => void reorder(item.id, -1)} disabled={pos <= 0} aria-label="Move up">
                            ↑
                          </button>
                          <button
                            type="button"
                            onClick={() => void reorder(item.id, 1)}
                            disabled={pos < 0 || pos >= (selectedIds ?? []).length - 1}
                            aria-label="Move down"
                          >
                            ↓
                          </button>
                        </span>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            );
          })}
          {materialsSay ? <p class="sp__age">{materialsSay}</p> : null}
        </div>
      )}
      {pageSlug ? <p class="sp__age">{`Your page: calltowrap.com/a/${pageSlug}`}</p> : null}

      {/* §6 — THE MESSAGE */}
      {/* B20d: the heading is "The email" — the button already says "Write
          the email", and a heading that repeats a button's own label is
          dead weight. */}
      <h2 class="sub__section-title">The email</h2>
      {!draft ? (
        <button type="button" class="sp__act" data-testid="write-email" disabled={draftBusy} onClick={() => void writeTheEmail()}>
          {draftBusy ? 'Writing…' : 'Write the email'}
        </button>
      ) : (
        <div class="sub__composer">
          <p class="sub__composer-line">
            {`From: ${subject?.display_name ?? ''} via CallToWrap`}
          </p>
          {mailbox ? <p class="sub__composer-line">{`Replies go to: ${mailbox}`}</p> : null}
          <p class="sub__composer-subject">{draft.subject}</p>
          {/* §6 — the email, shown VERBATIM as the server composed it. Never
              the message alone: this is what actually goes out. */}
          <p class="sub__composer-body" data-testid="draft-preview">
            {draft.preview}
          </p>
          {draftEditable ? (
            <div class="sub__composer-message-block">
              <textarea
                class="sub__composer-message"
                data-testid="draft-message-editable"
                value={draftMessage}
                onInput={(e) => setDraftMessage((e.target as HTMLTextAreaElement).value)}
              />
              <button
                type="button"
                class="sp__inline-act"
                data-testid="save-message"
                disabled={messageSaving}
                onClick={() => void saveMessage()}
              >
                {messageSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          ) : null}

          {canSend?.ok ? (
            <button type="button" class="sp__act" data-testid="send-submission" onClick={() => void doSend()}>
              Send
            </button>
          ) : canSend ? (
            <>
              <p class="sp__say sp__say--loud" data-testid="cansend-refusal">
                {canSend.message}
              </p>
              {canSend.reason === 'provenance_required' || canSend.reason === 'sending_disabled' ? (
                <button type="button" class="sp__inline-act" data-testid="copy-email" onClick={() => void copyEmailInstead()}>
                  Copy the email instead
                </button>
              ) : null}
            </>
          ) : null}
          {draftSay ? <p class="sp__age">{draftSay}</p> : null}
        </div>
      )}

      {/* §7 — LINKS AND RECEIPTS */}
      <h2 class="sub__section-title">Seen</h2>
      {linkSummaries === null ? (
        <p class="sp__say">One moment…</p>
      ) : linkSummaries.length === 0 ? (
        materialsFileIds.length > 0 ? (
          <button type="button" class="sp__inline-act" onClick={() => void mintFirstLink()}>
            Make a link
          </button>
        ) : (
          <p class="sp__say">Choose what to send first.</p>
        )
      ) : (
        <div class="sub__seen">
          {linkSummaries.map((summary) => (
            <div key={summary.grantId} class="sub__seen-link">
              {summary.receipts.map((r, i) => {
                const item = portfolio?.items.find((it) => it.id === r.fileId);
                const label = item ? GROUP_LABEL[item.group].toLowerCase().replace(/s$/, '') : 'file';
                const day = new Date(r.occurredAt).toLocaleDateString('en-US', {
                  weekday: 'short',
                  timeZone: 'UTC',
                });
                const time = new Date(r.occurredAt).toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit',
                  timeZone: 'UTC',
                });
                const who = contact?.display_name ?? 'Someone';
                return (
                  <p key={i} class="sp__age">
                    {r.event === 'opened'
                      ? `${who} opened your ${label} · ${day} ${time}`
                      : `${who} downloaded your ${label} · ${day} ${time}`}
                  </p>
                );
              })}
              {summary.status === 'expired' ? (
                <p class="sp__age">
                  tried an expired link · {new Date(summary.expiresAt).toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' })}
                  {' '}
                  <button type="button" class="sp__inline-act" onClick={() => void reissueLink(summary.grantId)}>
                    Send a fresh link
                  </button>
                </p>
              ) : null}
            </div>
          ))}
        </div>
      )}

      {api?.replies && api.replies.length > 0 ? (
        <div class="sub__replies">
          {api.replies.map((r) => (
            <button type="button" key={r.sourceId} class="sp__inline-act" onClick={() => onOpenSource(r.sourceId)}>
              {`${r.from} replied · ${new Date(r.receivedAt).toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' })}`}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );

  async function doSend(): Promise<void> {
    // F4's real /send-email door (already built, actorSubmissionSending.ts)
    // takes recipients + a message + F3's RAW token. B20c fix: that token
    // is the draft's OWN `linkToken` — one link, minted by `/draft`, reused
    // here. No second mint.
    const recipientEmail = strField(contact, 'email');
    if (!draft || !recipientEmail) return;
    try {
      const result = await sendSubmissionEmail(tenantId, id, {
        to: [recipientEmail],
        subject: draft.subject,
        message: draftEditable ? draftMessage : draft.message,
        linkToken: draft.linkToken,
      });
      if (result.sentTo.length > 0) {
        // The email really went — so the submission IS sent. send-email
        // records the send but never moves the stage (F1 seam); without this
        // a sent submission sat on Draft (walk, 2026-09-27).
        if (record?.lifecycle_state === 'draft') {
          try {
            await markSubmissionSentByHand(tenantId, id);
          } catch {
            /* the send itself stands; the stage can be moved by hand */
          }
        }
        setDraftSay(
          result.testRedirectTo
            ? `Test mode — sent to ${result.testRedirectTo}, not to ${contact?.display_name ?? recipientEmail} — ${opportunity}.`
            : `Sent to ${contact?.display_name ?? recipientEmail} — ${opportunity}.`
        );
        reload();
        bumpRecordsVersion();
        const rows = await listSubmissionLinksApi(tenantId, id);
        setLinkSummaries(rows);
      } else {
        setDraftSay(result.failed?.[0]?.message ?? 'that could not be sent');
      }
    } catch (err) {
      setDraftSay(err instanceof ApiError ? err.serverMessage ?? err.message : 'that could not be sent');
    }
  }

  async function mintFirstLink(): Promise<void> {
    if (materialsFileIds.length === 0) return;
    try {
      await mintSubmissionLinkApi(tenantId, id, materialsFileIds);
      const rows = await listSubmissionLinksApi(tenantId, id);
      setLinkSummaries(rows);
    } catch (err) {
      setMaterialsSay(err instanceof ApiError ? err.message : 'that link could not be made');
    }
  }
}
